import { differenceInMinutes } from 'date-fns';
// API
import { getSchedulerBookingSessions } from '@/features/bookings-management/api';
import {
  getLatestOnTheWayArrivalRisk,
  resolveBookingSessionBillableHours,
} from '@/features/bookings-management/utils/bookingSessions';
// types
import { BookingSession } from '@/features/bookings-management/types';
import { TransformedBooking } from '../types';

/**
 * Transforms an array of booking data into a specific format suitable for further processing or display.
 * Each booking is mapped to a new structure that includes parsed and formatted date and time,
 * title, resource, property details, address, recurrence information, and event type.
 *
 * @param {Booking[]} bookingData - An array of booking objects to be transformed.
 * @returns {Object[]} An array of transformed booking objects. Each object includes fields such as id,
 *                     start, end, title, resource, property, address, recurring, and eventType.
 *                     The `start` and `end` fields are generated based on booking time and duration.
 *                     The `title` defaults to the booking id if not present.
 *                     The `property` and `address` fields are constructed from the booking details.
 *                     Recurrence information is also included if available.
 *
 * @example
 * // Example of a booking object that might be transformed
 * const bookings = [{
 *   id: 1,
 *   time: '10:00',
 *   hours: 2,
 *   address: { line_1: '123 Main St', line_2: 'Apt 4', town_or_city: 'Anytown', postcode: '12345' },
 *   booking_recurrence: { from_date: '2024-01-01', repeat_string: 'Weekly', week_days: 'Mon,Wed,Fri' }
 * }];
 *
 * const transformedBookings = parseBookingData(bookings);
 * console.log(transformedBookings);
 */

/**
 * Parses and transforms an array of booking data into a structured format.
 * Each booking is formatted with detailed attributes including dates, address, recurrence, and more.
 *
 * @param {Booking[]} bookingData - An array of booking objects to be transformed.
 * @returns {TransformedBooking[]} An array of transformed booking objects.
 */

// export function parseBookingData(bookingData: Booking[]) {
//   // const filteredArray = bookingData.filter((booking) =>
//   //   booking.date.includes('2024-02-07')
//   // );
//   // return filteredArray.map((booking) => transformBooking(booking));
//   return bookingData.map((booking) => transformBooking(booking));
// }
export function parseBookingSessionData(
  bookingSessions: BookingSession[]
): TransformedBooking[] {
  const transformedSessions = bookingSessions.map((filteredSessions) =>
    transformBookingSession(filteredSessions)
  );

  return transformedSessions;
}

function addHoursToDate(date: Date, hours: number) {
  const result = new Date(date.getTime());
  const minutesToAdd = Number.isFinite(hours) ? hours * 60 : 0;
  result.setMinutes(result.getMinutes() + minutesToAdd);
  return result;
}

function createDateFromDateTime(dateString: string, timeString: string) {
  // Combine date and time strings
  const dateTimeString = `${dateString}T${timeString}`;

  return dateTimeString;
}

export function formatDateToISOString(dateString: Date, dateOnly?: boolean) {
  // Parse the original date string to create a Date object
  const date = new Date(dateString);

  // Format the date to ISO string in UTC ('YYYY-MM-DDTHH:mm:ss' format)
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');

  // Combine the components to match the desired format
  const formattedDate = `${year}-${month}-${day}`;
  const formattedDateAndTime =
    formattedDate + `T${hours}:${minutes}:${seconds}`;

  return dateOnly ? formattedDate : formattedDateAndTime;
}

const AIRBNB_ICON = 'logos:airbnb-icon';
const HOLIDAY_RENTAL_SERVICE_VARIANT_ID = 4;

const getPropertyTypeName = (propertyType: unknown) => {
  if (typeof propertyType === 'string') {
    return propertyType;
  }

  if (propertyType && typeof propertyType === 'object') {
    const propertyTypeRecord = propertyType as Record<string, unknown>;
    const propertyTypeName =
      propertyTypeRecord.name ??
      propertyTypeRecord.ref ??
      propertyTypeRecord.value ??
      propertyTypeRecord.display;

    return typeof propertyTypeName === 'string' ? propertyTypeName : '';
  }

  return '';
};

const getPropertyTypeKey = (propertyType: unknown) =>
  getPropertyTypeName(propertyType)
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');

export const assignPropertyTypeIcon = (propertyType: unknown) => {
  switch (getPropertyTypeKey(propertyType)) {
    case 'house':
      return 'ph:house-fill';
    case 'flat':
    case 'apartment':
      return 'vaadin:office';
    case 'guest_house':
      return 'ion:bed';
    case 'hotel':
      return 'fa6-solid:hotel';
    default:
      return 'eva:question-mark-outline';
  }
};

const isHolidayRentalSession = (session: BookingSession) =>
  Boolean(session.show_airbnb) ||
  Number(session.service_variant?.id) === HOLIDAY_RENTAL_SERVICE_VARIANT_ID;

const getSessionClientName = (session: BookingSession) =>
  [session.user?.first_name, session.user?.last_name]
    .map((name) => (typeof name === 'string' ? name.trim() : ''))
    .filter(Boolean)
    .join(' ');

const getSessionClientId = (session: BookingSession) => {
  const candidates = [
    session.user?.id,
    session.booking?.user?.id,
    session.booking?.user_id,
  ];
  const clientId = candidates.find((candidate) =>
    Number.isFinite(Number(candidate))
  );

  return clientId ? Number(clientId) : undefined;
};

const getSchedulerTitle = (session: BookingSession) => {
  const sessionTitle = `#${session.id}`;

  if (!isHolidayRentalSession(session)) {
    return sessionTitle;
  }

  const clientName = getSessionClientName(session);

  return clientName ? `${sessionTitle} - ${clientName}` : sessionTitle;
};

const getSchedulerProperty = (session: BookingSession) => {
  if (isHolidayRentalSession(session)) {
    return {
      type: 'Airbnb',
      icon: AIRBNB_ICON,
      iconTitle: 'Airbnb',
    };
  }

  const propertyTypeName = getPropertyTypeName(session.property_type);

  return {
    type: propertyTypeName,
    icon: assignPropertyTypeIcon(session.property_type),
    iconTitle: propertyTypeName || 'Unknown property type',
  };
};

// TODO: have Chris+Elena confirm this
const SERVICE_LABELS: Record<
  number,
  {
    code: string;
    description: string;
  }
> = {
  1: { code: 'PO', description: 'Personal one-off' },
  2: { code: 'PW', description: 'Personal weekly' },
  3: { code: 'PF', description: 'Personal fortnightly' },
  4: { code: 'HO', description: 'Holiday rental one-off' },
  5: { code: 'OF', description: 'Office' },
};

const DEFAULT_SERVICE_LABEL = {
  code: '??',
  description: 'Unknown service type',
};

export const SERVICE_LABEL_LEGEND = Object.values(SERVICE_LABELS);

export const getServiceLabelInfo = (
  serviceVariantId?: number | null
): { code: string; description: string } => {
  if (!serviceVariantId) {
    return DEFAULT_SERVICE_LABEL;
  }

  return SERVICE_LABELS[serviceVariantId] ?? DEFAULT_SERVICE_LABEL;
};

const getSessionServiceName = (session: BookingSession) => {
  const serviceName = session.service_variant?.service?.name?.trim();

  return serviceName || null;
};

const normalizeDisplayText = (value?: string | null) => {
  if (!value) return null;

  const normalized = value.trim().replace(/[_-]+/g, ' ');

  return normalized || null;
};

const getSessionServiceDisplay = (
  session: BookingSession,
  fallbackDescription: string
) =>
  getSessionServiceName(session) ?? normalizeDisplayText(fallbackDescription);

const getVariantRecurrenceDisplay = (value?: string | null) => {
  const normalized = normalizeDisplayText(value)?.toLowerCase();

  if (!normalized) return null;
  if (normalized.includes('fortnight')) return 'fortnightly recurring';
  if (normalized.includes('weekly')) return 'weekly recurring';
  if (normalized.includes('recurring')) return normalized;
  if (normalized.includes('one off') || normalized.includes('one-off')) {
    return 'one-off';
  }

  return null;
};

const getBookingRecurrenceDisplay = (session: BookingSession) => {
  const recurrence = session.booking_recurrence;
  const repeatString = recurrence?.repeat_string?.toLowerCase();
  const intervalNumber = Number(recurrence?.interval_number);

  if (
    !repeatString ||
    !Number.isFinite(intervalNumber) ||
    intervalNumber <= 0
  ) {
    return null;
  }

  if (repeatString === 'weekly' && intervalNumber === 2) {
    return 'fortnightly recurring';
  }

  if (repeatString === 'weekly') {
    return 'weekly recurring';
  }

  return `${repeatString} recurring`;
};

const getSessionRecurrenceDisplay = (
  session: BookingSession,
  fallbackDescription: string
) =>
  getBookingRecurrenceDisplay(session) ??
  getVariantRecurrenceDisplay(session.service_variant?.name) ??
  getVariantRecurrenceDisplay(fallbackDescription);

/**
 * Transforms a single booking object into a structured format.
 *
 * @param {Booking} booking - A booking object to be transformed.
 * @returns {TransformedBooking} A transformed booking object.
 */

export function transformBookingSession(
  session: BookingSession
): TransformedBooking {
  const scheduledStartDate = new Date(
    createDateFromDateTime(session.date, session.time)
  );
  const actualStartDate = session.start_time
    ? new Date(session.start_time)
    : null;
  const validScheduledStart = !Number.isNaN(scheduledStartDate.getTime())
    ? scheduledStartDate
    : null;
  const validActualStart =
    actualStartDate && !Number.isNaN(actualStartDate.getTime())
      ? actualStartDate
      : null;
  const startDelayMinutes =
    validActualStart && validScheduledStart
      ? differenceInMinutes(validActualStart, validScheduledStart)
      : null;

  const serviceLabelInfo = getServiceLabelInfo(session.service_variant?.id);
  const assignedCleaners = session.booking_session_cleaners ?? [];
  const hasAssignedCleaner = assignedCleaners.length > 0;
  const hasAcceptedCleaner = assignedCleaners.some(
    (cleaner) => cleaner.accepted_by_provider === true
  );
  const awaitingProviderAcceptance = hasAssignedCleaner && !hasAcceptedCleaner;
  const billableHours = resolveBookingSessionBillableHours(session);
  const latestArrivalRisk = getLatestOnTheWayArrivalRisk(session);
  const hasArrivalRiskWarning =
    latestArrivalRisk?.status === 'at_risk' ||
    latestArrivalRisk?.status === 'late';
  const statusStyleClass = session.booking_session_status?.style_class;
  const serviceDisplay = getSessionServiceDisplay(
    session,
    serviceLabelInfo.description
  );
  const recurrenceDisplay = getSessionRecurrenceDisplay(
    session,
    serviceLabelInfo.description
  );

  const transformedBookingSession = {
    id: session.id,
    start: createDateFromDateTime(session.date, session.time),
    end: formatDateToISOString(
      addHoursToDate(
        new Date(createDateFromDateTime(session.date, session.time)),
        billableHours
      )
    ),
    title: getSchedulerTitle(session),
    resource: session.booking_session_cleaners[0]?.id || 0,
    property: getSchedulerProperty(session),
    address: {
      id: session.address?.id,
      line1: session.address?.line_1 || '',
      line2: session.address?.line_2 || '',
      city: session.address?.town_or_city || '',
      postcode: session.address?.postcode || '',
    },
    clientUserId: getSessionClientId(session),
    serviceLabel: serviceLabelInfo.code,
    serviceLabelDescription:
      getSessionServiceName(session) ?? serviceLabelInfo.description,
    serviceDisplay,
    recurrenceDisplay,
    cssClass: statusStyleClass,
    statusDisplay: session.booking_session_status?.display,
    statusValue: session.booking_session_status?.value,
    statusStyleClass,
    serviceTypeId: session.service_variant.service.id,
    totalHours: billableHours,
    editable: true,
    awaitingProviderAcceptance,
    arrivalRisk: latestArrivalRisk,
    hasArrivalRiskWarning,
    actualStartTime: validActualStart ? validActualStart.toISOString() : null,
    startDelayMinutes,
    isTestAccount: Boolean(session.user?.test_account),
  };

  // if (
  //   session.booking_recurrence &&
  //   session.booking_recurrence.interval_number > 0
  // ) {
  //   Object.assign(transformedBookingSession, {
  //     recurring: {
  //       repeat: session.booking_recurrence.repeat_string || '',
  //       weekDays: session.booking_recurrence.week_days
  //         ? session.booking_recurrence.week_days.replaceAll('|', ',')
  //         : '',
  //       interval: session.booking_recurrence.interval_number || 0,
  //       from: session.booking_recurrence.from_date || '',
  //       until: session.booking_recurrence.until || '',
  //     },
  //   });
  // }
  // debugger;
  return transformedBookingSession;
}

/**
 * Asynchronously fetches and transforms booking data. If a start date is provided,
 * it fetches bookings filtered from that date. Otherwise, it fetches all bookings.
 *
 * @param {string} [startDate] - An optional start date in 'YYYY-MM-DD' format to filter bookings.
 *                               If not provided, all bookings are fetched.
 * @returns {Promise<BookingTransformed[]>} A promise that resolves to an array of transformed booking data.
 *
 * @example
 * // Fetch all bookings
 * fetchAndTransformBookings().then(data => console.log(data));
 *
 * @example
 * // Fetch bookings from a specific start date
 * fetchAndTransformBookings('2024-01-01').then(data => console.log(data));
 */
// export const fetchAndTransformBookings = async (
//   startDate?: string
// ): Promise<TransformedBooking[]> => {
//   let bookings: Booking[] = [];

//   if (!startDate) {
//     bookings = await getBookings();
//   } else {
//     bookings = await getFilteredBookings('2024-01-01');
//   }
//   return parseBookingData(bookings);
// };
export const fetchAndTransformBookingSessions = async (
  params?: string
): Promise<TransformedBooking[]> => {
  let bookingSessions: BookingSession[] = [];
  bookingSessions = await getSchedulerBookingSessions(params);

  return parseBookingSessionData(bookingSessions);
};
